import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Sparkles, AlertTriangle, Filter, HelpCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Spinner, EmptyState } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// AssistantAuditScreen — /admin/assistant  (T-447 + T-468)
// DIRECTOR ONLY. Every question put to Ask Coretel, what it cost, and — most
// usefully — the ones that came back with nothing.
//
// On "no result": RLS deliberately makes "you can't see it" and "it doesn't
// exist" indistinguishable, so we don't claim to detect permission denials.
// An empty answer means either the person couldn't see it or we haven't written
// the guide — reading the question tells you which, and both are worth knowing.
// =============================================================================

export default function AssistantAuditScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [only, setOnly] = useState<"all" | "no_result">("all");

  const canSee = hasRole(user, "director");

  const { data: threads = [] } = useQuery({
    queryKey: ["assistant_audit_threads"],
    enabled: !!canSee,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("assistant_threads")
        .select("thread_id, owner_user_id, title, context_route, created_at")
        .order("created_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: msgs = [], isLoading } = useQuery({
    queryKey: ["assistant_audit_msgs"],
    enabled: !!canSee,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("assistant_messages")
        .select("message_id, thread_id, role, content, model, prompt_tokens, completion_tokens, est_cost_usd, outcome, created_at")
        .order("created_at", { ascending: false })
        .limit(600);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: userMap = {} } = useQuery({
    queryKey: ["assistant_audit_users"],
    enabled: !!canSee,
    queryFn: async () => {
      const { data } = await supabase.from("users").select("user_id, login_email").limit(200);
      const m: Record<string, string> = {};
      for (const u of (data ?? []) as any[]) m[u.user_id] = u.login_email;
      return m;
    },
  });

  const threadMap = useMemo(() => {
    const m: Record<string, any> = {};
    for (const t of threads) m[t.thread_id] = t;
    return m;
  }, [threads]);

  // Pair each assistant answer with the question immediately before it.
  const exchanges = useMemo(() => {
    const asc = [...msgs].sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
    const out: any[] = [];
    let lastQ: any = null;
    for (const m of asc) {
      if (m.role === "user") lastQ = m;
      else if (m.role === "assistant") {
        out.push({
          id: m.message_id,
          thread_id: m.thread_id,
          question: lastQ?.content ?? "(question not captured)",
          answer: m.content,
          model: m.model,
          tokens: (m.prompt_tokens ?? 0) + (m.completion_tokens ?? 0),
          cost: Number(m.est_cost_usd ?? 0),
          outcome: m.outcome ?? "ok",
          at: m.created_at,
        });
        lastQ = null;
      }
    }
    return out.reverse();
  }, [msgs]);

  const shown = useMemo(
    () => (only === "no_result" ? exchanges.filter(e => e.outcome === "no_result") : exchanges),
    [exchanges, only]
  );

  const kpi = useMemo(() => ({
    questions: exchanges.length,
    unanswered: exchanges.filter(e => e.outcome === "no_result").length,
    errors: exchanges.filter(e => e.outcome === "error").length,
    cost: exchanges.reduce((a, e) => a + e.cost, 0),
    people: new Set(threads.map(t => t.owner_user_id)).size,
  }), [exchanges, threads]);

  if (!user) return null;
  if (!canSee) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={AlertTriangle} title="Directors only"
          description="The assistant audit is restricted to directors." /></Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto pb-12">
      <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> Home
      </button>

      <h1 className="text-[22px] font-semibold text-text flex items-center gap-2">
        <Sparkles size={20} className="text-accent" /> Assistant activity
      </h1>
      <p className="text-[13px] text-text-muted mt-1 mb-5">
        Every question put to Ask Coretel. The unanswered ones are the useful list — each is either a guide we haven't written or something that person can't see.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Kpi label="Questions"   value={kpi.questions} />
        <Kpi label="Unanswered"  value={kpi.unanswered} tone={kpi.unanswered ? "warn" : undefined} />
        <Kpi label="Errors"      value={kpi.errors} tone={kpi.errors ? "danger" : undefined} />
        <Kpi label="People"      value={kpi.people} />
        <Kpi label="Est. cost"   value={`$${kpi.cost.toFixed(2)}`} />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Filter size={13} className="text-text-muted" />
        <Chip on={only === "all"} onClick={() => setOnly("all")}>Everything</Chip>
        <Chip on={only === "no_result"} onClick={() => setOnly("no_result")}>Unanswered only</Chip>
      </div>

      <Card>
        <div className="p-5">
          {isLoading ? (
            <div className="py-10 flex justify-center"><Spinner /></div>
          ) : shown.length === 0 ? (
            <EmptyState icon={HelpCircle}
              title={only === "no_result" ? "Nothing unanswered" : "No questions yet"}
              description={only === "no_result"
                ? "Every question so far found something. That's a good sign."
                : "Once people start using Ask Coretel, their questions appear here."} />
          ) : (
            <div className="divide-y divide-line/50">
              {shown.map(e => {
                const t = threadMap[e.thread_id];
                return (
                  <div key={e.id} className="py-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="text-[13px] text-text flex-1 min-w-0">
                        {e.outcome === "no_result" && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-warn mr-2 align-middle">
                            <AlertTriangle size={10} /> no answer
                          </span>
                        )}
                        {e.question}
                      </div>
                      <div className="text-[10px] text-text-dim font-mono whitespace-nowrap">
                        {e.tokens} tok · ${e.cost.toFixed(4)}
                      </div>
                    </div>
                    <p className="text-[12px] text-text-muted mt-1.5 line-clamp-3 whitespace-pre-wrap">{e.answer}</p>
                    <div className="text-[10px] text-text-dim mt-1">
                      {userMap[t?.owner_user_id] ?? "unknown"}
                      {t?.context_route ? <> · from <span className="font-mono">{t.context_route}</span></> : null}
                      {" · "}{e.model}
                      {" · "}{fmtDate(e.at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] border ${on ? "border-accent bg-accent/10 text-text" : "border-line text-text-muted hover:bg-ink-hover"}`}>
      {children}
    </button>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number | string; tone?: "warn" | "danger" }) {
  const c = tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : "text-text";
  return (
    <Card><div className="p-4">
      <div className="text-[10px] uppercase tracking-wider text-text-muted font-mono">{label}</div>
      <div className={`mt-1 text-[18px] font-semibold font-mono ${c}`}>{value}</div>
    </div></Card>
  );
}
